import { useEffect, useMemo, useState } from "react";

import { Box, IconButton, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsIcon from "@mui/icons-material/Settings";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty, isEqual, isNull, isString } from "lodash";

import companyService from "@plannel/services/data/company-service";
import locationService from "@plannel/services/data/location-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";

import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import CalendarModal from "@plannel/components/modal/CalendarModal";
import Dialog from "@plannel/components/Dialog";
import AbcXyzChip from "@plannel/components/aggrid/AbcXyzChip";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import StatPill from "@plannel/components/ui/StatPill";
import StyledSwitch from "@plannel/components/ui/StyledSwitch";
import { PANEL, PanelDashedButton } from "@plannel/components/ui/SidePanel";

import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import NumberUtil from "@plannel/utils/number-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

/*
 * 일괄 값 변경 모달 — 규칙 하나가 카드 하나다.
 *
 * 예전에는 본문이 ag-grid 였다. 컬럼 9개(범위 6 + 변경 3)의 폭 합이 1,300px 인데 모달 본문은 800px
 * 이라, 정작 이 모달의 목적인 Set Value / Increase-Decrease Value 가 가로 스크롤 뒤에 숨었다.
 * 정렬·필터·그룹·가상스크롤을 하나도 쓰지 않는 "행 몇 개짜리 폼"이었으므로 그리드를 걷어냈다
 * (config-modal 스킬의 ThresholdModal·DimensionModal 과 같은 판단).
 *
 * 카드 = 규칙 = 서버로 가는 행 하나. 위쪽은 **적용 범위**(어디에), 아래 웰은 **변경**(무엇을 어떻게)이고,
 * 값 컨트롤은 Update Target 에 따라 달라진다 — 숫자/텍스트 입력, 드롭다운, 스위치, 캘린더 선택.
 * 어휘는 AdvancedFilter 의 조건 카드와 맞췄다(PANEL 토큰, radius 9 알약 필드, 작은 대문자 라벨).
 *
 * ⚠ 저장 페이로드는 그리드 시절과 **완전히 같다** — service.bulkValueUpdate(행 배열).
 *   범위 필드의 "전체"는 null 로 접히고(location/itemHrchy/itemAttr/invClassCd), itemAttr 는 컬럼명
 *   문자열로, itemType 은 cd 로 바뀐다. 이 변환이 buildUpdateData 한 곳에 모여 있다.
 */

const { P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM } = CodeUtil.rpPolicyCd;
const RP_POLICY_LIST = [P_SYSTEM, Q_SYSTEM, MIN_MAX_SYSTEM, BASE_STOCK_SYSTEM];
const INV_CLASS_CD_LIST = CodeUtil.dpABCConfig;
const RP_FILTER_LOCATION = "RP_FILTER_LOCATION";
const RP_FILTER_CATEGORY = "RP_FILTER_CATEGORY";
const RP_FILTER_ITEMATTR = "RP_FILTER_ITEMATTR";
const { snackBarMessageColor } = ColorUtil;

// 값이 참/거짓뿐인 대상 — 입력칸이 아니라 스위치로 받는다(대상을 고르면 기본 true, 그리드 시절과 동일)
const BOOLEAN_TARGETS = ["ipApplied", "rpApplied", "ipFixedFlg", "activeFlg", "cycleInvFlg", "mpApplied"];
// 값을 CalendarModal 에서 고르는 대상 — 직접 입력은 불가
const CALENDAR_TARGETS = ["orderCalendar", "demandCalendar"];
const INTEGER_COLUMNS = ["opInvDays", "targetSupplyDays", "moq", "ioq", "safetyInvQty", "cycleInvQty"];
const RATIONAL_COLUMNS = ["otdLtD"];
const NUMERIC_COLUMNS = [...INTEGER_COLUMNS, ...RATIONAL_COLUMNS];
// 키팅 로케이션 주의가 필요한 대상 — 그리드 시절엔 헤더 툴팁에만 있었다
const WARNING_COLUMNS = ["ioq", "moq", "orderMethod", "orderCalendar", "otdLtD"];

/*
 * 범위·값 입력칸 — AdvancedFilter 조건 행과 같은 규격(34px / radius 9 / 옅은 채움).
 * outlined 테두리를 지우고 채움으로 대신한다(라벨 노치가 알약 모양과 싸운다).
 */
const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    height: 34,
    borderRadius: "9px",
    bgcolor: PANEL.wellBg,
    fontSize: FS.TITLE,
    color: PANEL.ink,
    "& fieldset": { border: 0 },
    "&:hover fieldset": { border: 0 },
    "&.Mui-focused": { boxShadow: `inset 0 0 0 2px ${PANEL.accentLineHover}` },
    "&.Mui-focused fieldset": { border: 0 },
  },
  "& .MuiSelect-icon": { color: PANEL.inkMuted },
  "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: PANEL.inkFaint },
};

// 값이 채워진 칸은 흰 바탕 + 청록 테두리로 — "전체"로 비워 둔 칸과 한눈에 갈린다
const FIELD_ON_SX = {
  ...FIELD_SX,
  "& .MuiOutlinedInput-root": {
    ...FIELD_SX["& .MuiOutlinedInput-root"],
    bgcolor: "#fff",
    boxShadow: `inset 0 0 0 1px ${PANEL.accentLine}`,
  },
};

const FIELD_LABEL_SX = {
  fontSize: FS.CAPTION, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase",
  color: PANEL.inkLabel, lineHeight: 1,
};

const CARD_SX = {
  borderRadius: "12px", bgcolor: "#fff", p: "12px 12px 14px",
  boxShadow: `inset 0 0 0 1px ${PANEL.lineSoft}`,
};

const WELL_SX = {
  mt: 1.5, borderRadius: "10px", bgcolor: PANEL.wellBg, p: 1.25,
  boxShadow: `inset 0 0 0 1px ${PANEL.lineSoft}`,
};

const REMOVE_SX = {
  p: 0.25, flex: "none", color: PANEL.iconFaint,
  "& > svg": { fontSize: 18 },
  "&:hover": { color: PANEL.danger, bgcolor: "transparent" },
};

let ruleSeq = 0;
const nextRuleKey = () => {
  ruleSeq += 1;
  return `rule-${ruleSeq}`;
};

const isNumericTarget = target => NUMERIC_COLUMNS.includes(target);

const RpBulkValueUpdateModal = (props) => {
  const { t, addedTargetColumnDefs = [{}], updateTargets, service, modalOpen, setModalOpen, selectedPlanVersion, retrieve, setSnackInfo, setProgressOn, moduleCd, useSiteAccess = false } = props;

  const reduxDispatch = useDispatch();
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [itemAttrs, setItemAttrs] = useState([]);
  const [abcXyzClass, setAbcXyzClass] = useState([]);

  const [rules, setRules] = useState([]);
  const [calendarRuleKey, setCalendarRuleKey] = useState(null);
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const allLocation = { locCd: null, name: t("all") };
  const allCategory = { hrchyCd: null, name: t("all") };
  const allItemAttr = { column: null, name: t("all") };

  /*
   * 호출측이 넘기는 추가 범위 필드(RP Policy·Run RP 의 itemType).
   * 그리드 시절의 columnDef 를 그대로 받으므로 여기서 카드 필드로 옮겨 읽는다 —
   * 호출측 API 를 바꾸면 3화면을 같이 고쳐야 해서 계약은 그대로 뒀다.
   * 기본값이 `[{}]` 라 field 없는 껍데기가 섞여 들어온다 → 걸러낸다.
   */
  const extraScopeFields = useMemo(
    () => (addedTargetColumnDefs ?? []).filter(colDef => colDef?.field),
    [addedTargetColumnDefs]
  );

  const extraOptions = colDef => colDef?.cellEditorParams?.values ?? [];

  // colDef 의 valueFormatter 를 그대로 태운다(호출측이 "전체" 표기까지 정해 뒀다)
  const formatExtra = (colDef, value) => {
    if (colDef?.valueFormatter) {
      return colDef.valueFormatter({ value });
    }

    return value?.name ?? value ?? t("all");
  };

  const setAbcXyzClassData = () => {
    setAbcXyzClass([t("all"), ...INV_CLASS_CD_LIST]);
  }

  const setLocationData = () => {
    const locationReduxViewState = reduxUtil.getViewState(RP_FILTER_LOCATION);
    if (locationReduxViewState?.location) {
      setLocations([allLocation, ...locationReduxViewState.location]);
    } else {
      let locationDatas = [];
      locationService.getLookup()
        .then(response => {
          const { results } = response.data;
          locationDatas = results;
          setLocations([allLocation, ...results]);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const locationReduxState = reduxUtil.createObjectState(RP_FILTER_LOCATION, { location: locationDatas });
          reduxDispatch(reduxUtil.updateViewState(locationReduxState));
        });
    }
  }

  const setCategoryData = () => {
    let categoryDatas = [];

    const categoryReduxViewState = reduxUtil.getViewState(RP_FILTER_CATEGORY);
    if (categoryReduxViewState?.category) {
      categoryDatas = categoryReduxViewState.category;
      setCategories(categoryDatas);
    } else {
      const params = { hrchyNum: 1 };
      itemHrchyService.getLookup(params)
        .then(response => {
          const { results } = response.data;
          categoryDatas = results;
          categoryDatas.unshift(allCategory);
          setCategories(categoryDatas);
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const categoryReduxState = reduxUtil.createObjectState(RP_FILTER_CATEGORY, { category: categoryDatas });
          reduxDispatch(reduxUtil.updateViewState(categoryReduxState));
        });
    }
  }

  const setItemAttrData = () => {
    let customItemAttrDatas = [];

    const itemAttrReduxState = reduxUtil.getViewState(RP_FILTER_ITEMATTR);

    if (itemAttrReduxState?.itemAttr) {
      customItemAttrDatas = itemAttrReduxState.itemAttr;
    } else {
      companyService.getCustomConfig()
        .then(response => {
          const { item } = response.data;
          if (item) {
            Object.keys(item).forEach(key => {
              const itemAttrObj = {}
              itemAttrObj.column = key;
              itemAttrObj.name = item[key];

              customItemAttrDatas.push(itemAttrObj);
            });

          }
        })
        .catch(e => {
          debug(e);
        })
        .then(() => {
          const itemAttrReduxState = reduxUtil.createObjectState(RP_FILTER_ITEMATTR, { itemAttr: customItemAttrDatas });
          reduxDispatch(reduxUtil.updateViewState(itemAttrReduxState));
        });
    }

    customItemAttrDatas.unshift(allItemAttr);
    setItemAttrs(customItemAttrDatas);

  }

  const createRule = () => {
    const rule = {
      key: nextRuleKey(),
      planVersion: selectedPlanVersion ? selectedPlanVersion : null,
      location: allLocation,
      itemHrchy: allCategory,
      itemAttr: allItemAttr,
      invClassCd: t("all"),
      itemCd: null,
      baseDate: null,
      updateTarget: null,
      updateValue: null,
      additionalValue: null
    };

    // 추가 범위 필드는 그리드 시절에도 초기값이 없었다(undefined → 저장 시 null)
    for (const colDef of extraScopeFields) {
      rule[colDef.field] = null;
    }

    return rule;
  }

  const updateRule = (key, patch) => {
    setRules(prev => prev.map(rule => (isEqual(rule.key, key) ? { ...rule, ...patch } : rule)));
  }

  const addRule = () => setRules(prev => [...prev, createRule()]);

  const removeRule = key => setRules(prev => prev.filter(rule => !isEqual(rule.key, key)));

  const numberValueFormatter = (val, digits) => {
    const value = NumberUtil.formatNumber(val, { maximumFractionDigits: digits });
    return Number(value);
  }

  /*
   * 숫자 대상의 값 정규화 — 그리드 시절 onCellValueChanged 가 셀 편집 확정(=blur) 때 하던 일이다.
   * 정수 대상은 소수점을 버리고, otdLtD 만 소수 1자리를 남긴다.
   */
  const normalizeNumeric = (target, raw) => {
    if (raw === "" || isNull(raw) || raw === undefined) {
      return null;
    }

    const numberValue = Number(raw);
    if (!isFinite(numberValue) || isNaN(numberValue)) {
      return null;
    }

    if (INTEGER_COLUMNS.includes(target)) {
      return numberValueFormatter(raw, 0);
    } else if (RATIONAL_COLUMNS.includes(target)) {
      return numberValueFormatter(raw, 1);
    }

    return numberValue;
  }

  const changeTarget = (rule, target) => {
    if (isEqual(rule.updateTarget, target)) {
      return;
    }

    // 대상이 바뀌면 값 컨트롤 자체가 바뀐다 — 이전 값은 버린다(그리드 시절과 동일)
    updateRule(rule.key, {
      updateTarget: target,
      updateValue: BOOLEAN_TARGETS.includes(target) ? true : null,
      additionalValue: null
    });
  }

  /*
   * 저장 페이로드 만들기 — 그리드 시절 bulkValueUpdate 앞부분의 변환·검증을 그대로 옮겼다.
   * 유효하지 않으면 스낵바를 띄우고 null 을 돌려준다.
   */
  const buildUpdateData = () => {
    const updateData = rules.map(({ key, ...rest }) => ({ ...rest }));

    for (const data of updateData) {
      const { updateTarget, updateValue } = data;
      if (isEmpty(updateTarget)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoSelectedUpdateTarget") });
        return null;
      }

      if (isEqual(updateTarget, "orderMethod") && isEmpty(updateValue)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_MissingInput") });
        return null;
      }

      if (NUMERIC_COLUMNS.includes(updateTarget)) {
        if (isEqual(updateValue, "")) {
          data.updateValue = null;
        } else if (!isNull(updateValue)) {
          if (isString(updateValue)) {
            const trimValue = updateValue.trim();
            data.updateValue = isEqual(trimValue, "") ? null : trimValue;
          } else {
            data.updateValue = Number(updateValue);
          }
        }
      }

      if (isNull(data.location.locCd)) {
        data.location = null;
      }

      if (isNull(data.itemHrchy.hrchyCd)) {
        data.itemHrchy = null;
      }

      if (isEqual(data.invClassCd, t("all"))) {
        data.invClassCd = null;
      }

      if (!data.itemType) {
        data.itemType = null;
      } else {
        if (isEqual(data.itemType.cd, "all")) {
          data.itemType = null;
        } else {
          data.itemType = data.itemType.cd;
        }
      }

      if (isNull(data.itemAttr.column)) {
        data.itemAttr = null;
      } else {
        data.itemAttr = data.itemAttr.column;
      }

      if (data.itemAttr && isEmpty(data.attrValue)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_MissingInput") });

        return null;
      }

      data.moduleCd = moduleCd;
      data.useSiteAccess = useSiteAccess;
    }

    return updateData;
  }

  const bulkValueUpdate = () => {
    const updateData = buildUpdateData();
    if (!updateData) {
      return;
    }

    setProgressOn(true);
    service.bulkValueUpdate(updateData)
      .then(response => {
        const { planVersion } = response.data;

        setProgressOn(false);
        setModalOpen(false);
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });

        if (planVersion && !isEmpty(planVersion)) {
          retrieve(planVersion);
        } else {
          retrieve();
        }
      })
      .catch(e => {
        const { data, status } = e.response;
        const errorMessage = data?.message ?? "MSG_SaveFailed";

        if (status === 409) {
          const optimisticErrorMessage = t("MSG_OptimisticLockAboutTarget", { target: updateData[0].planVersion.name });
          setSnackInfo({ open: true, severity: snackBarMessageColor.optimisticLock, content: optimisticErrorMessage });
        } else {
          const content = data?.variables ? t(errorMessage, { itemCodes: data.variables }) : t(errorMessage);
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content });
        }

        setProgressOn(false);
        debug(e);
      });
  }

  /*
   * 닫을 때 확인을 물을지 — 그리드 시절엔 "행이 하나라도 있으면" 물었는데, 열자마자 빈 행이 하나
   * 생기므로 아무것도 안 건드려도 항상 물었다. 손댄 게 있을 때만 묻는다.
   */
  const isDirty = () => {
    if (rules.length > 1) {
      return true;
    }

    return rules.some(rule => (
      !isNull(rule.location?.locCd) || !isNull(rule.itemHrchy?.hrchyCd) || !isNull(rule.itemAttr?.column)
      || !isEqual(rule.invClassCd, t("all")) || !isEmpty(rule.itemCd) || !isEmpty(rule.attrValue)
      || !isNull(rule.updateTarget) || extraScopeFields.some(colDef => !isEmpty(rule[colDef.field]))
    ));
  }

  const handleClose = action => {
    if (isEqual(action, "cancel")) {
      if (isDirty()) {
        setDialogInfo({ open: true, title: "close", content: t("MSG_ConfirmToClose") });
      } else {
        setModalOpen(false);
      }
    } else if (isEqual(action, "save")) {
      bulkValueUpdate();
    }
  }

  const handleDialog = (action) => {
    if (isEqual(action, "CLOSE")) {
      setModalOpen(false);
    }
  }

  const targetLabel = target => (isEqual(target, "cycleInvFlg") ? t("cycleStockApplied") : t(target));

  const getNumericValueString = () => {
    const currentNumericColumns = [];
    for (const column of updateTargets) {
      if (NUMERIC_COLUMNS.includes(column)) {
        currentNumericColumns.push(t(column));
      }
    }

    return currentNumericColumns.join(", ");
  }

  // 카드 머리의 한 줄 요약 — 접힌 카드가 아니라도 "이 규칙이 어디에 걸리는지"를 먼저 읽게 한다
  const ruleSummary = rule => {
    const scopes = [];
    if (!isNull(rule.location?.locCd)) scopes.push(rule.location.name);
    if (!isNull(rule.itemHrchy?.hrchyCd)) scopes.push(rule.itemHrchy.name);
    if (!isEqual(rule.invClassCd, t("all"))) scopes.push(rule.invClassCd);
    if (!isNull(rule.itemAttr?.column)) scopes.push(`${rule.itemAttr.name}${rule.attrValue ? ` = ${rule.attrValue}` : ""}`);
    if (!isEmpty(rule.itemCd)) scopes.push(rule.itemCd);
    for (const colDef of extraScopeFields) {
      if (rule[colDef.field]) scopes.push(formatExtra(colDef, rule[colDef.field]));
    }

    const scopeText = isEmpty(scopes) ? t("all") : scopes.join(" · ");

    return rule.updateTarget ? `${scopeText} · ${targetLabel(rule.updateTarget)}` : scopeText;
  }

  const calendarRule = rules.find(rule => isEqual(rule.key, calendarRuleKey));

  /*
   * CalendarModal 은 ag-grid 셀 파라미터(`editedCell.node.setDataValue`)로 값을 돌려준다.
   * 그리드가 없어졌으므로 같은 모양의 껍데기를 만들어 넘긴다 — CalendarModal 쪽은 손대지 않는다
   * (CycleCalendarRenderer 를 포함해 다른 그리드 6곳이 그대로 쓰고 있다).
   */
  const calendarCell = useMemo(() => {
    if (!calendarRule) {
      return null;
    }

    return {
      value: calendarRule.updateValue,
      column: "updateValue",
      node: { setDataValue: (_column, value) => updateRule(calendarRule.key, { updateValue: value }) }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarRule]);

  useEffect(() => {
    if (modalOpen) {
      setRules([createRule()]);
      setLocationData();
      setCategoryData();
      setAbcXyzClassData();
      setItemAttrData();
    } else {
      setRules([]);
      setCalendarRuleKey(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  const renderScopeField = (label, control, key) => (
    <Box key={key} sx={{ display: "grid", gap: 0.5, minWidth: 0 }}>
      <Box sx={FIELD_LABEL_SX}>{label}</Box>
      {control}
    </Box>
  );

  /*
   * 범위 드롭다운 — 객체 목록이라 index 를 value 로 쓴다(객체를 value 로 주면 참조가 어긋난다).
   * 목록은 모달을 열면서 비동기로 채워지므로, 아직 비어 있는 동안에도 표시값은 고른 값 이름으로 낸다
   * (renderValue 가 없으면 그 사이 칸이 빈 채로 깜빡인다).
   */
  const renderObjectSelect = (options, selected, matchKey, onSelect, renderOption) => {
    const selectedIndex = options.findIndex(option => isEqual(option?.[matchKey], selected?.[matchKey]));
    const optionLabel = option => (renderOption ? renderOption(option) : option?.name);

    return (
      <Select
        size="small"
        displayEmpty
        value={selectedIndex < 0 ? "" : selectedIndex}
        onChange={event => onSelect(options[event.target.value])}
        sx={isNull(selected?.[matchKey]) || selected?.[matchKey] === undefined ? FIELD_SX : FIELD_ON_SX}
        renderValue={index => (index === "" || index < 0 ? (selected?.name ?? t("all")) : optionLabel(options[index]))}
      >
        {options.map((option, index) => (
          <MenuItem key={`${option?.[matchKey] ?? "all"}-${index}`} value={index} sx={{ fontSize: FS.TITLE }}>
            {renderOption ? renderOption(option) : option?.name}
          </MenuItem>
        ))}
      </Select>
    );
  }

  const renderValueControl = rule => {
    const { updateTarget, updateValue } = rule;

    // 대상을 고르기 전에는 값 칸이 무엇을 받을지 정해지지 않는다 — 비활성 빈 칸으로 둔다
    if (isNull(updateTarget)) {
      return <TextField size="small" disabled value="" sx={FIELD_SX} />;
    }

    if (BOOLEAN_TARGETS.includes(updateTarget)) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 34 }}>
          <StyledSwitch
            checked={Boolean(updateValue)}
            onChange={event => updateRule(rule.key, { updateValue: event.target.checked })}
          />
          <Box sx={{ fontSize: FS.TITLE, color: PANEL.ink }}>{updateValue ? t("on") : t("off")}</Box>
        </Box>
      );
    }

    if (CALENDAR_TARGETS.includes(updateTarget)) {
      return (
        <Box sx={{ ...FIELD_SX["& .MuiOutlinedInput-root"], display: "flex", alignItems: "center", gap: 0.5, px: 1, ...(updateValue ? { bgcolor: "#fff", boxShadow: `inset 0 0 0 1px ${PANEL.accentLine}` } : {}) }}>
          <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: updateValue ? PANEL.ink : PANEL.inkFaint }}>
            {updateValue?.name ? updateValue.name : t("notConfigured")}
          </Box>
          <IconButton size="small" aria-label="settings" onClick={() => setCalendarRuleKey(rule.key)}>
            <SettingsIcon sx={{ width: 15, height: 15 }} />
          </IconButton>
        </Box>
      );
    }

    if (isEqual(updateTarget, "orderMethod")) {
      return (
        <Select
          size="small"
          displayEmpty
          value={updateValue ?? ""}
          onChange={event => updateRule(rule.key, { updateValue: event.target.value })}
          sx={isEmpty(updateValue) ? FIELD_SX : FIELD_ON_SX}
          renderValue={value => (isEmpty(value) ? "" : GridUtils.gridValueL10N(value))}
        >
          {RP_POLICY_LIST.map(policy => (
            <MenuItem key={policy} value={policy} sx={{ fontSize: FS.TITLE }}>{GridUtils.gridValueL10N(policy)}</MenuItem>
          ))}
        </Select>
      );
    }

    const numeric = isNumericTarget(updateTarget);

    return (
      <TextField
        size="small"
        type={numeric ? "number" : "text"}
        value={isNull(updateValue) || updateValue === undefined ? "" : updateValue}
        onChange={event => updateRule(rule.key, { updateValue: event.target.value })}
        onBlur={event => numeric && updateRule(rule.key, { updateValue: normalizeNumeric(updateTarget, event.target.value) })}
        sx={{
          ...(isEmpty(String(updateValue ?? "")) ? FIELD_SX : FIELD_ON_SX),
          "& .MuiInputBase-input": { fontSize: FS.TITLE, textAlign: numeric ? "right" : "left" }
        }}
      />
    );
  }

  const renderRule = (rule, index) => {
    const numeric = isNumericTarget(rule.updateTarget);
    const warning = WARNING_COLUMNS.includes(rule.updateTarget) && isEqual(moduleCd, CodeUtil.module.RP);

    return (
      <Box key={rule.key} sx={CARD_SX}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
          <StatPill label={t("rule")} value={index + 1} accent />
          <Box sx={{ minWidth: 0, flex: 1, fontSize: FS.SUB, color: PANEL.inkMuted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {ruleSummary(rule)}
          </Box>
          <IconButton size="small" sx={REMOVE_SX} disabled={rules.length < 2} onClick={() => removeRule(rule.key)}>
            <DeleteOutlineIcon />
          </IconButton>
        </Box>

        {/* 적용 범위 — 비워 두면(전체) 회색 채움, 고르면 흰 바탕 + 청록 테두리 */}
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px 10px" }}>
          {renderScopeField(t("location"), renderObjectSelect(locations, rule.location, "locCd", value => updateRule(rule.key, { location: value })))}
          {renderScopeField(t("category"), renderObjectSelect(categories, rule.itemHrchy, "hrchyCd", value => updateRule(rule.key, { itemHrchy: value })))}
          {renderScopeField(t("invClassCd"), (
            <Select
              size="small"
              value={rule.invClassCd ?? t("all")}
              onChange={event => updateRule(rule.key, { invClassCd: event.target.value })}
              sx={isEqual(rule.invClassCd, t("all")) ? FIELD_SX : FIELD_ON_SX}
              renderValue={value => (isEqual(value, t("all")) ? value : <AbcXyzChip value={value} />)}
            >
              {(isEmpty(abcXyzClass) ? [t("all")] : abcXyzClass).map(invClass => (
                <MenuItem key={invClass} value={invClass} sx={{ fontSize: FS.TITLE }}>
                  {isEqual(invClass, t("all")) ? invClass : <AbcXyzChip value={invClass} />}
                </MenuItem>
              ))}
            </Select>
          ))}
          {renderScopeField(t("itemAttr"), renderObjectSelect(itemAttrs, rule.itemAttr, "column", value => updateRule(rule.key, { itemAttr: value, attrValue: null })))}
          {renderScopeField(t("attrValue"), (
            <TextField
              size="small"
              disabled={isNull(rule.itemAttr?.column) || !rule.itemAttr}
              placeholder={t("all")}
              value={rule.attrValue ?? ""}
              onChange={event => updateRule(rule.key, { attrValue: event.target.value })}
              sx={{ ...(isEmpty(rule.attrValue) ? FIELD_SX : FIELD_ON_SX), "& .MuiInputBase-input": { fontSize: FS.TITLE } }}
            />
          ))}
          {renderScopeField(t("itemCd"), (
            <TextField
              size="small"
              placeholder={t("all")}
              value={rule.itemCd ?? ""}
              onChange={event => updateRule(rule.key, { itemCd: event.target.value })}
              sx={{ ...(isEmpty(rule.itemCd) ? FIELD_SX : FIELD_ON_SX), "& .MuiInputBase-input": { fontSize: FS.TITLE } }}
            />
          ))}
          {extraScopeFields.map(colDef => renderScopeField(t(colDef.headerName), (
            <Select
              size="small"
              displayEmpty
              value={rule[colDef.field] ? extraOptions(colDef).findIndex(option => isEqual(option, rule[colDef.field])) : ""}
              onChange={event => updateRule(rule.key, { [colDef.field]: extraOptions(colDef)[event.target.value] })}
              sx={rule[colDef.field] ? FIELD_ON_SX : FIELD_SX}
              renderValue={optionIndex => formatExtra(colDef, optionIndex === "" || optionIndex < 0 ? null : extraOptions(colDef)[optionIndex])}
            >
              {extraOptions(colDef).map((option, optionIndex) => (
                <MenuItem key={`${colDef.field}-${optionIndex}`} value={optionIndex} sx={{ fontSize: FS.TITLE }}>
                  {formatExtra(colDef, option)}
                </MenuItem>
              ))}
            </Select>
          ), colDef.field))}
        </Box>

        {/* 변경 — 무엇을 어떻게. 값 컨트롤은 대상에 따라 달라진다 */}
        <Box sx={WELL_SX}>
          <Box sx={{ display: "grid", gridTemplateColumns: numeric ? "1fr 1fr 1fr" : "1fr 2fr", gap: "8px 10px" }}>
            {renderScopeField(
              <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
                {t("updateTarget")}
                {warning && (
                  <Tooltip title={t("MSG_KittingLocationWarning")} arrow placement="right">
                    <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: FS.SUB, color: "primary.main", cursor: "help" }} />
                  </Tooltip>
                )}
              </Box>,
              (
                <Select
                  size="small"
                  displayEmpty
                  value={rule.updateTarget ?? ""}
                  onChange={event => changeTarget(rule, event.target.value)}
                  sx={isNull(rule.updateTarget) ? FIELD_SX : FIELD_ON_SX}
                  renderValue={value => (isEmpty(value) ? "" : targetLabel(value))}
                >
                  {updateTargets.map(target => (
                    <MenuItem key={target} value={target} sx={{ fontSize: FS.TITLE }}>{targetLabel(target)}</MenuItem>
                  ))}
                </Select>
              )
            )}
            {renderScopeField(t("setValue"), renderValueControl(rule))}
            {numeric && renderScopeField(
              <Tooltip title={t("MSG_EnterOnlyValues", { values: getNumericValueString() })} arrow placement="top">
                <Box component="span">{t("additionalValue")}</Box>
              </Tooltip>,
              (
                <TextField
                  size="small"
                  type="number"
                  value={isNull(rule.additionalValue) || rule.additionalValue === undefined ? "" : rule.additionalValue}
                  onChange={event => updateRule(rule.key, { additionalValue: event.target.value })}
                  onBlur={event => updateRule(rule.key, { additionalValue: normalizeNumeric(rule.updateTarget, event.target.value) })}
                  sx={{
                    ...(isNull(rule.additionalValue) ? FIELD_SX : FIELD_ON_SX),
                    "& .MuiInputBase-input": { fontSize: FS.TITLE, textAlign: "right" }
                  }}
                />
              )
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <BaseConfigModal
        open={modalOpen}
        onClose={() => handleClose("cancel")}
        title={t("bulkValueUpdate")}
        onSave={() => handleClose("save")}
        titleAdornment={<StatPill component="span" label={t("rule")} value={rules.length} accent />}
        width={720}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {rules.map(renderRule)}
          <PanelDashedButton icon={<AddIcon />} onClick={addRule}>{t("addRule")}</PanelDashedButton>
        </Box>
      </BaseConfigModal>
      {/* 캘린더를 고르는 동안에만 띄운다 — 상시 마운트하면 이 모달을 쓰는 3화면이 열릴 때마다
          CalendarModal 이 함께 만들어진다(달력 대상을 한 번도 안 고르는 경우가 대부분이다) */}
      {calendarRule && (
        <CalendarModal
          showDetail
          modalOpen
          setModalOpen={open => setCalendarRuleKey(open ? calendarRuleKey : null)}
          editedCell={calendarCell}
          setSnackInfo={setSnackInfo}
        />
      )}
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
    </>
  );
}

export default withTranslation()(RpBulkValueUpdateModal);
